import { CommonModule, DatePipe } from '@angular/common';
import { Component, DestroyRef, inject, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { I18nService } from '../core/i18n.service';
import { TeamsApiService } from '../core/teams-api.service';
import { TeamInvitationItem } from '../core/teams-api.types';

@Component({
  selector: 'app-player-invitations-page',
  standalone: true,
  imports: [CommonModule, DatePipe, RouterLink],
  template: `
    <section class="page">
      <h1>{{ t('player.invitations.title') }}</h1>

      @if (loading()) {
        <p class="state">{{ t('player.invitations.loading') }}</p>
      } @else if (errorMessage()) {
        <p class="state state--error">{{ errorMessage() }}</p>
      } @else if (invitations().length === 0) {
        <article class="empty">
          <p><strong>{{ t('player.invitations.none.title') }}</strong></p>
          <p class="state">{{ t('player.invitations.none.subtitle') }}</p>
          <p class="state"><a routerLink="/player/teams">{{ t('player.teams.title') }}</a> · <a routerLink="/player/teams/create">{{ t('player.teams.create') }}</a></p>
        </article>
      } @else {
        <ul class="list">
          @for (invitation of invitations(); track invitation.id) {
            <li class="card">
              <p><strong>{{ invitation.team_name }}</strong></p>
              <p>{{ t('player.invitations.from', { value: invitation.invited_by_pseudo }) }}</p>
              <p>{{ t('player.invitations.role', { value: invitation.invited_role }) }}</p>
              <p>{{ t('player.invitations.date', { value: (invitation.created_at | date: 'short') ?? '' }) }}</p>
              <button type="button" [disabled]="acceptingId() === invitation.id" (click)="accept(invitation)">
                {{ acceptingId() === invitation.id ? t('player.invitations.accepting') : t('player.invitations.accept') }}
              </button>
            </li>
          }
        </ul>
      }
    </section>
  `,
  styles: `
    .page { padding: 1rem; max-width: 720px; margin: 0 auto; }
    h1 { margin: 0 0 0.75rem; font-size: 1.4rem; color: var(--text-strong); }
    .list { list-style: none; margin: 0; padding: 0; display: grid; gap: 0.75rem; max-width: 640px; }
    .card { border: 1px solid var(--line-soft); border-radius: 10px; padding: 0.75rem; background: linear-gradient(180deg, rgba(23, 36, 58, 0.94) 0%, rgba(18, 29, 47, 0.94) 100%); box-shadow: var(--shadow-soft); }
    .empty { border: 1px solid var(--line-soft); border-radius: 10px; padding: 0.75rem; background: linear-gradient(180deg, rgba(23, 36, 58, 0.94) 0%, rgba(18, 29, 47, 0.94) 100%); max-width: 640px; box-shadow: var(--shadow-soft); }
    .card p { margin: 0.25rem 0; color: var(--text-muted); }
    .card strong, .empty strong { color: var(--text-strong); }
    button { margin-top: 0.4rem; border: 1px solid rgba(138, 184, 255, 0.6); border-radius: 8px; padding: 0.55rem 0.8rem; background: linear-gradient(180deg, var(--accent) 0%, var(--accent-strong) 100%); color: #091325; font-weight: 700; }
    .state { color: var(--text-muted); }
    .state--error { color: var(--danger); }
    a { color: var(--accent); text-decoration: none; font-weight: 600; }
  `
})
export class PlayerInvitationsPageComponent {
  private readonly teamsApi = inject(TeamsApiService);
  private readonly router = inject(Router);
  private readonly i18n = inject(I18nService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly invitations = signal<TeamInvitationItem[]>([]);
  protected readonly loading = signal(true);
  protected readonly acceptingId = signal<number | null>(null);
  protected readonly errorMessage = signal<string | null>(null);

  constructor() {
    this.loadInvitations();
  }

  protected accept(invitation: TeamInvitationItem): void {
    this.acceptingId.set(invitation.id);
    this.errorMessage.set(null);

    this.teamsApi
      .acceptInvitation(invitation.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ team }) => {
          this.acceptingId.set(null);
          void this.router.navigateByUrl(`/player/teams/${team.id}`);
        },
        error: (error) => {
          const apiError = error?.error?.error;
          const apiErrors = error?.error?.errors;
          this.errorMessage.set(
            (typeof apiError === 'string' && apiError) ||
              (Array.isArray(apiErrors) && apiErrors.length > 0
                ? apiErrors.join(' | ')
                : this.t('player.invitations.acceptError'))
          );
          this.acceptingId.set(null);
        }
      });
  }

  private loadInvitations(): void {
    this.loading.set(true);
    this.errorMessage.set(null);

    this.teamsApi
      .getMyInvitations()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ invitations }) => {
          this.invitations.set(invitations);
          this.loading.set(false);
        },
        error: () => {
          this.errorMessage.set(this.t('player.invitations.loadError'));
          this.loading.set(false);
        }
      });
  }
  protected t(key: string, params?: Record<string, string | number | null | undefined>): string {
    return this.i18n.t(key, params);
  }
}
